//@ts-nocheck

// NOTE: intentionally NOT a 'use client' module. ApiService — and every service
// that extends it — is isomorphic: all browser access is guarded with
// `typeof window`/`typeof document`, so server components (the sitemap, the
// public advocate-blog pages) can import and run it. Marking this 'use client'
// turned its exports into client-reference proxies inside the server bundle,
// which crashed `next build` with `class X extends ApiService` →
// "Class extends value #<Object> is not a constructor".
import { HOST } from '@/lib/config';
import { mfaStepPath } from '@/lib/utils/mfaRedirect';
import { devLog } from '@/utils/devLog';
import axios, {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

export interface ApiResp<T = any> {
  data: T;
  // 'partial' — batch operation where some items succeeded and some were
  // skipped (e.g. POST /storage/move, #7455); callers must branch on it.
  status: 'success' | 'partial' | 'error' | 'errors';
  message: string;
  meta: {
    total?: number;
    limit?: number;
    page?: number;
    [key: string]: any;
  };
  errors: [];
  code: number;
}

export type ApiResponse<T = any> = Promise<ApiResp<T>>;

/**
 * Latch so the MFA hard-redirect below fires at most once per page load.
 *
 * Module scope is deliberate and safe: `window.location.href` unloads the
 * document, which tears down this module with it, so the flag cannot leak
 * across navigations and strand a later legitimate redirect.
 */
let mfaRedirectInFlight = false;

/** `code` of the 403 the MFA gate answers with (see the redirect branch below). */
const MFA_REQUIRED_CODE = 'mfa_required';

export const apiClient = axios.create({
  baseURL: HOST,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Only access sessionStorage in browser environment
    if (typeof window !== 'undefined' && sessionStorage) {
      const sessionId = sessionStorage.getItem('sessionId');
      if (sessionId) {
        config.headers.Authorization = `Bearer ${sessionId}`;
      }
    }

    // Attach the current client page path on every request so the backend can
    // reliably know the originating page. The browser's Referer header is
    // path-stripped (origin-only) by default Referrer-Policy and unreliable;
    // this app-set header is not subject to that, so the backend always sees
    // the real path (e.g. /sk/analyza-dokumentu).
    if (typeof window !== 'undefined' && window.location) {
      config.headers['X-Client-Page'] = window.location.pathname;
    }

    return config;
  }
);
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const data = response?.data;
    // Optional-chained: a 200 whose body is literally `null` is a legitimate
    // response shape — `GET /cases/{id}/knowledge-base` answers `null` for a
    // spis that never had a knowledge base built (response_model
    // `CaseKnowledgeBaseRecord | None`). Without the guard that body throws a
    // TypeError inside this interceptor and the call REJECTS, turning a
    // documented empty result into an indistinguishable failure.
    if (data?.status === 'error') {
      devLog('Backend rejected request response due to SUCCESS being false');
      return data;
    }
    return data;
  },
  (error: AxiosError) => {
    if (error.response) {
      const { data, status } = error.response as any;

      // Per-request opt-in (same mechanism as `rejectOn403` below): reject any
      // 4xx so the caller sees the real HTTP status on `error.response.status`.
      // By default a resolved 401/404/422 body carries NO status code
      // (FastAPI answers a bare `{detail}`), so a caller that must tell "bad
      // link" from "other failure" cannot do it from the resolved value.
      // Two exceptions keep going to their own branches below:
      // - 429 reaches the normalization, which resolves `code: 429` plus the
      //   Retry-After hint, so an opted-in caller can still honour the back-off.
      // - a 403 `mfa_required` reaches the MFA redirect branch (which also
      //   rejects), so opting in never swallows the redirect.
      if (
        error.config?.rejectOnClientError &&
        status >= 400 &&
        status < 500 &&
        status !== 429 &&
        !(status === 403 && data?.code === MFA_REQUIRED_CODE)
      ) {
        return Promise.reject(error);
      }

      // 429: rate-limited. nginx's @rate_limited handler answers with
      // `{"detail": ...}` + a Retry-After header (nginx/sites/api.lexomat.sk.conf),
      // which doesn't fit the ApiResp envelope — normalize it into one so
      // callers can branch on `code === 429` and honor the back-off hint
      // instead of misreading the throttle as an empty/failed search (#8290).
      if (status === 429) {
        const retryAfterSeconds = Number(
          error.response.headers?.['retry-after']
        );
        // `detail` is a string on nginx's @rate_limited body but a structured
        // object on backend HTTPException 429s (e.g. the per-user
        // /professionals/matches rate limit, whose consumer reads
        // `detail.bucket` / `detail.retry_after_seconds` —
        // professionalsSlice.ts::contactProfessional). Pass it through
        // untouched and keep `message` a guaranteed string.
        const detail = data?.detail;
        const detailMessage =
          typeof detail === 'string'
            ? detail
            : typeof detail?.message === 'string'
              ? detail.message
              : undefined;
        return Promise.resolve({
          data: null,
          status: 'error',
          message: detailMessage ?? data?.message ?? 'Rate limit exceeded',
          meta: Number.isFinite(retryAfterSeconds) ? { retryAfterSeconds } : {},
          errors: [],
          code: 429,
          ...(detail !== undefined && { detail }),
        });
      }

      // IMPORTANT: Reject these statuses so they surface to callers' try-catch
      // blocks instead of silently resolving (an outage must NOT look like success).
      // 402/409: quota/limit errors that need special handling in the UI
      // 410: resource expired/gone (e.g. shared intake document TTL elapsed) — callers
      //      must branch on status, not on error-message wording (#6203)
      // 500: server errors that should never be silently swallowed
      // 502/503/504: gateway/upstream errors. 503 was already here; 502 + 504 added
      //      in #8368 — 7+ backend route files reachable from the frontend raise 502
      //      (billing, stripe_subscriptions, checkout, blog, session_shares,
      //      document_generation, professionals/intake, professionals/shared_document),
      //      and the previous resolve-on-502 meant an upstream outage was silently
      //      treated as an empty/failed result (e.g. get_available_org_plans'
      //      `res.data?.plans ?? []` degraded "gracefully" — masking the outage).
      if (
        status === 402 ||
        status === 409 ||
        status === 410 ||
        status === 500 ||
        status === 502 ||
        status === 503 ||
        status === 504
      ) {
        return Promise.reject(error);
      }

      // MFA required: redirect to verification page. Backend emits a
      // top-level `{detail, code}` shape from the global auth middleware
      // (server/middleware.py). Future MFA gates land here by adding to
      // the allow-list, not by parsing a new body shape.
      if (status === 403 && data?.code === MFA_REQUIRED_CODE) {
        // Don't hard-redirect while the user is anywhere in the MFA flow —
        // any background fetch (auth-context, quota probe, etc.) that hits
        // an MFA-gated endpoint while the user is still AAL1 would otherwise
        // bounce window.location back to /mfa/verify and remount the page,
        // which triggers the same fetch again and loops indefinitely.
        //
        // The guard originally named ONLY /mfa/verify, which left the loop
        // wide open one page over: an advocate with no verified factor is
        // sent to /mfa/enroll, its background fetches 403 with
        // `mfa_required`, this interceptor hard-navigates to /mfa/verify,
        // MfaChallenge finds no VERIFIED factor and router.replace()s back to
        // /mfa/enroll — and round it goes. Observed as a permanently
        // "refreshing" page after a profile claim, with the enrollment QR
        // never rendering, i.e. MFA could not be completed at all. That is
        // the step SAK makes mandatory for advocate accounts (Metodické
        // usmernenie SAK k Uzneseniu č. 21/14/2025, čl. 6 ods. 3 písm. b).
        //
        // Prefix-match the whole /mfa/ area (verify, enroll, recover): every
        // page in it is a place an AAL1 user is legitimately parked, so none
        // of them may be yanked away by a background 403. Locale-prefixed
        // paths (/sk/mfa/...) are covered by the substring test.
        // Fire the navigation ONCE. A page load issues several MFA-gated
        // requests in parallel (auth-context's /users/me, /users/profile,
        // /professionals/my-profile, the quota probe…), and every one of them
        // lands here. Without this latch each assigns window.location.href in
        // turn, so the browser starts and aborts a navigation per response —
        // a visible burst of "refreshing" before it finally settles. Observed
        // on /sk/chat as ~5 aborted /mfa/verify navigations from a single load.
        if (
          typeof window !== 'undefined' &&
          !window.location.pathname.includes('/mfa/') &&
          !mfaRedirectInFlight
        ) {
          mfaRedirectInFlight = true;
          // On the MCP OAuth consent screen the pending authorization must
          // survive the MFA step (#12062), so its path rides along as
          // `?redirect=`. `mfaStepPath` sanitizes and allow-lists it: every
          // other page still gets the bare `/mfa/verify`, exactly as before.
          window.location.href = mfaStepPath(
            'verify',
            `${window.location.pathname}${window.location.search}`
          );
        }
        return Promise.reject(error);
      }

      // #11755 follow-up: a plain 403 (no `mfa_required` code — a real
      // permission denial, e.g. POST /conversation/cancel/{id} answering
      // "Access denied") falls through to the generic resolve-with-data
      // path below by default, same as any other unlisted status. That is
      // deliberate for most callers, but it means a caller that specifically
      // needs to DISTINGUISH "denied" from "any other outcome" cannot do so
      // by catching — a real browser check caught exactly this: the body
      // never throws, so a `try/catch` branching on `err.response.status`
      // never ran, no matter how correct that branch's own code was.
      //
      // Per-request opt-in rather than widening the reject-list above: axios
      // forwards unrecognized config keys onto `error.config`, so a caller
      // that sets `rejectOn403: true` in its request config gets a real
      // rejection for its OWN 403s, and every other caller — none of which
      // has asked for this — keeps the exact resolve-with-data behaviour it
      // already depends on. Never flip this to an unconditional reject.
      if (status === 403 && error.config?.rejectOn403) {
        return Promise.reject(error);
      }

      if (status === 401 || status === 404) {
        // Expected control flow, not a fault: 401 is every anonymous visitor's
        // first authed call, and 404 is how endpoints answer "no such record"
        // (`/professionals/my-profile` fires on every page for non-advocates).
        // Callers branch on the resolved envelope below — logging these at
        // error level filled the production console with non-events.
        devLog(status === 401 ? 'Not authorized' : 'Resource not found', error);
      } else if (status === 500) {
        console.error('Server error:', data?.detail ?? data);
      } else {
        console.error(data.message);
      }
      return Promise.resolve(error.response.data);
    } else if (error.request) {
      console.error('Network error!', error);
    } else {
      console.error(`Error ${error.message}`);
    }
    // Dev-only: `error.config` carries the full request config — headers, and
    // with them the Authorization bearer token. It has no place in a
    // production console.
    devLog('Error config:', error.config);

    return Promise.reject(error);
  }
);

class ApiService {
  private baseUrl = '';
  constructor(baseUrl = '') {
    if (baseUrl && !baseUrl.startsWith('http')) {
      baseUrl = `${HOST}${baseUrl}`;
    }
    this.baseUrl = baseUrl;
  }

  getUrl(path: string | undefined) {
    if (!path?.startsWith('http')) {
      return `${this.baseUrl}${path}`;
    }
    return path;
  }

  getHeaders() {
    return {
      'Content-type': 'application/json; charset=UTF-8',
    };
  }

  getData(path: string, options?: any): ApiResponse {
    const headers = options?.headers || this.getHeaders();
    return apiClient.get(this.getUrl(path), { ...options, headers });
  }

  postData(path: string, data: any, options?: any): ApiResponse {
    const headers = options?.headers || this.getHeaders();

    return apiClient.post(this.getUrl(path), data, { ...options, headers });
  }

  putData(path: string, data: any, options?: any): ApiResponse {
    const headers = options?.headers || this.getHeaders();

    return apiClient.put(this.getUrl(path), data, { ...options, headers });
  }

  patchData(path: string, data: any, options?: any): ApiResponse {
    const headers = options?.headers || this.getHeaders();

    return apiClient.patch(this.getUrl(path), data, { ...options, headers });
  }

  deleteData(path: string, data: any, options?: any): ApiResponse {
    const headers = options?.headers || this.getHeaders();

    return apiClient.delete(this.getUrl(path), {
      data,
      ...options,
      headers,
    });
  }
}

export default ApiService;
